// AnalysisShortcutBar
//
// Icon / label buttons that deep-link any "scope" (a customer, a saved
// filter, or a single facility) into one of the analysis pages with the
// scope already pre-selected. Used at the bottom of every box on the
// My Customers, My Filters, and All Facilities pages — and, when
// `activated`, inside the green ScopeBar on analysis pages so users can
// hop between reports without losing the customer/filter they picked.
//
// The destination pages each read the same URL parameters on mount —
// `customerId`, `filterId`, or `facilityId` — and apply the corresponding
// selection + auto-load. CustomerData also accepts the legacy `groupId`
// alias so existing in-app links keep working.
//
// `context.kind`  : 'customer' | 'filter' | 'facility'
// `context.id`    : the underlying ID (customer PK, filter PK, or canonical
//                   integer-string VitalEngine facility ID)
// `context.label` : optional human-readable name for the tooltip
// `activated`     : when true, outline the whole block and mark the
//                   current route as the selected report (with a → arrow)

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import './AnalysisShortcutBar.css';

const PARAM_KEY = {
  customer: 'customerId',
  filter:   'filterId',
  facility: 'facilityId',
};

// Order matches the sidebar (App.js NAV_ITEMS) so users build muscle
// memory: per-scope shortcut buttons line up with the global nav.
const SHORTCUTS = [
  { path: '/overview',                label: 'Overview',                icon: '🗂' },
  { path: '/customer-data',           label: 'Data Trends',             icon: '📈' },
  { path: '/collaboration-analytics', label: 'Collaboration Analytics', icon: '🔗' },
  { path: '/insights',                label: 'Insights',                icon: '💡' },
  { path: '/study-analytics',         label: 'DICOM Studies',           icon: '🩻' },
  { path: '/analytics',               label: 'Raw Data',                icon: '📊' },
  { path: '/quick-refer',             label: 'Quick Refer',             icon: '⚡' },
  { path: '/users',                   label: 'User Reports',            icon: '👤' },
  { path: '/tableau-reports',         label: 'Tableau Reports',         icon: '📋' },
];

function AnalysisShortcutBar({
  context,
  compact = false,
  stopPropagation = true,
  label,
  activated = false,
}) {
  const location = useLocation();
  if (!context || !context.id || !PARAM_KEY[context.kind]) return null;

  const param = PARAM_KEY[context.kind];
  const id = encodeURIComponent(String(context.id));
  const scopeName = context.label ? ` for ${context.label}` : '';

  // When the bar lives inside a clickable card (e.g. a Welcome customer
  // card wrapped in a <Link>), every shortcut click would otherwise also
  // trigger the card's parent navigation. Stop the bubble unless the
  // caller knows it isn't nested.
  const handleClick = stopPropagation
    ? (e) => e.stopPropagation()
    : undefined;

  const classes = [
    'analysis-shortcut-bar',
    compact ? 'compact' : '',
    activated ? 'activated with-labels' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={classes}
      role="group"
      aria-label={activated
        ? 'Scoped reports — switch report while keeping this scope'
        : 'Open this scope in another analysis view'}
      onClick={handleClick}
    >
      {label && <span className="analysis-shortcut-bar-label">{label}</span>}
      {activated && !label && (
        <span className="analysis-shortcut-bar-label">Scoped reports</span>
      )}
      {SHORTCUTS.map(s => {
        const isCurrent = location.pathname === s.path;
        return (
          <Link
            key={s.path}
            to={`${s.path}?${param}=${id}`}
            className={`analysis-shortcut ${isCurrent ? 'is-current' : ''}`}
            title={`${s.label}${scopeName}`}
            aria-label={`${s.label}${scopeName}`}
            aria-current={isCurrent ? 'page' : undefined}
            onClick={handleClick}
          >
            <span className="analysis-shortcut-icon" aria-hidden="true">{s.icon}</span>
            <span className="analysis-shortcut-label">{s.label}</span>
            {isCurrent && activated && (
              <span className="analysis-shortcut-arrow" aria-hidden="true">→</span>
            )}
          </Link>
        );
      })}
    </div>
  );
}

export default AnalysisShortcutBar;
